import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, CardHeader, CardActions, CardContent, List, Typography, Button, FormControlLabel, TextField, Stack, Pagination, Select, SelectChangeEvent, MenuItem, useTheme } from '@mui/material';
import { useTranslation } from "react-i18next";
import { Add } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DashboardEditDialog from "components/widgetParts/DashboardEditDialog";
import APIRequest, { APIError } from "system/APIRequest";
import { DashboardDocument, DashboardListResponse } from "components/resources/dashboards/DashboardDocument";
import withOptionsWidget from "components/widgetParts/withOptionsWidget";
import InsufficientPermissionsDialog from "components/dialogs/InsufficientPermissionsDialog";
import { currentUser } from "system/InfegyUser";
import { DashboardListItem } from "./DashboardListItem";
import AlignedSwitch from "components/uiElements/inputs/AlignedSwitch";
import { TextLoader } from "components/apiHandling/TextLoader";

enum DashboardType {
  All = "all",
  Single = "template",
  Multi = "null"
}

interface DashboardTypeOption {
  label: string;
  value: DashboardType;
}

export function DashboardListContent(): JSX.Element {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const user = currentUser();

  const [dashboardList, setDashboardList] = useState<Array<DashboardDocument>>([]);
  const [dashboardListResponse, setDashboardListResponse] = useState<DashboardListResponse>();
  const [dashboardListIsLoading, setDashboardListIsLoading] = useState<boolean>(false);
  const [globalDashboardList, setGlobalDashboardList] = useState<Array<DashboardDocument>>([]);
  const [globalDashboardListResponse, setGlobalDashboardListResponse] = useState<DashboardListResponse>();
  const [newDashboardDialogIsOpen, setNewDashboardDialogIsOpen] = useState<boolean>(false);
  const [viewShared, setViewShared] = useState<boolean>((user?.role === "quicksearch" || user?.role === "view-only") ? true : false);
  const [searchString, setSearchString] = useState<string | undefined>(undefined);
  const [showInsufficientPermissionsDialog, setShowInsufficientPermissionsDialog] = useState<boolean>(false);
  const [dashboardType, setDashboardType] = useState<DashboardType>((user?.role === "quicksearch" || user?.role === "view-only") ? DashboardType.Multi : DashboardType.All);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const dashboardsPerPage = 20;
  const globalDashboardTotal = globalDashboardListResponse?.pagination?.total ?? globalDashboardList.length;
  const pageStartIndex = (currentPage - 1) * dashboardsPerPage;
  const pageEndIndex = pageStartIndex + dashboardsPerPage;
  const sharedUserStartIndex = Math.max(pageStartIndex - globalDashboardTotal, 0);
  const sharedUserEndIndex = Math.max(pageEndIndex - globalDashboardTotal, 0);

  const numberOfDashboards = (dashboardListResponse?.pagination?.total ?? 0) + globalDashboardTotal;
  const totalPages = Math.ceil(numberOfDashboards / dashboardsPerPage) || 0;

  const canSubmitQueries = user?.permissions?.includes("run_query");
  const canAddDashboards = user?.permissions?.includes("create_dashboard");
  const isGettingStarted = canAddDashboards && (dashboardList.length === 0 && !searchString?.length && totalPages < 2);

  const dashboardTypeOptions: Array<DashboardTypeOption> = useMemo(() => [{
    label: t("all"),
    value: DashboardType.All
  }, {
    label: t("dashboard.freeform"),
    value: DashboardType.Multi
  }, {
    label: t("dashboard.template"),
    value: DashboardType.Single
  }], []);

  useEffect(() => {
    setDashboardListIsLoading(true);
    // Fetch global dashboards
    const searchObj = [
      {"id":"global_access","op":"=","value":"1"}
    ];

    const globalRequestOptions:any = {
      "per_page":10,
      "sort":"updated_at",
      "sort_order":"desc",
      "include_shared": 1,
      "q": JSON.stringify(searchObj)
    };

    APIRequest("obj/dashboard", globalRequestOptions).then((response: any) => {
      setGlobalDashboardListResponse(response);
      setGlobalDashboardList(response.output);
      setDashboardListIsLoading(false);
    }).catch((apiError: APIError) => {
      enqueueSnackbar(`${apiError.message}: ${apiError.server_message}`, {variant: 'error', persist: true});
      setDashboardListIsLoading(false);
    });
  }, []);

  const fetchDashboards = useCallback(() => {
    const searchList = [];
    const params:any = {
      per_page: dashboardsPerPage,
      page: currentPage,
      sort:"is_favorite,last_viewed_by_you",
      sort_order:"desc",
      schema: 1
    };
    if (viewShared) {
      params["include_shared"] = 1;
      params["page"] = 1;
      params["per_page"] = Math.max(sharedUserEndIndex, dashboardsPerPage);
    }
    searchList.push({"id":"global_access","op":"=","value":"0"});

    if (searchString && searchString.length > 1) {
      searchList.push({"id":"name","op":"contains","value":searchString});
    }
    if (!canSubmitQueries) {
      // If the user can't submit queries, only show dashboards that are not template dashboards
      searchList.push({"id":"template_type","op":"null","value":""});
    } else {
      if (dashboardType === DashboardType.Single) {
        searchList.push({id: "template_type", op: "=", value: "single"});
      } else if (dashboardType === DashboardType.Multi) {
        searchList.push({id: "template_type", op: "null", value: ""});
      }
    }
    if (searchList.length > 0) {
      params["q"] = JSON.stringify(searchList);
    }

    return APIRequest("obj/dashboard", params).then((response: any) => {
      const resources: Array<DashboardDocument> = [];
      setDashboardListResponse(response);
      // Pull out the dashboards from the response
      response['output'].forEach((obj: any) => {
        resources.push(obj);
      });
      setDashboardList(resources);
      return resources;
    }).catch((apiError: APIError) => {
      enqueueSnackbar(`${apiError.message}: ${apiError.server_message}`, {variant: 'error', persist: true});
    });
  }, [viewShared, searchString, currentPage, dashboardType]);

  const memoizedDashboards = useMemo(() => {
    return fetchDashboards();
  }, [fetchDashboards, viewShared]);

  useEffect(() => {
    if (dashboardList?.length > 0)
      return;
    memoizedDashboards.then((resources:any) => {
      setDashboardList(resources || []);
    });
  }, [dashboardList?.length, memoizedDashboards]);

  const dashboardRows = dashboardList?.map((dashboard)=>{
    return (
      <DashboardListItem
        key={dashboard.id}
        dashboard={dashboard}
        reloadDashboardList={fetchDashboards}
        permissionList={dashboardListResponse?.permissions}
        />
    );
  });

  const globalDashboardOutputRows = globalDashboardList?.map((dashboard) => {
    const duplicatePermissions = user?.permissions?.includes("create_dashboard") ? ["duplicate", "create"] : [];
    return (
      <DashboardListItem
        key={dashboard.id}
        dashboard={dashboard}
        permissionList={[...(globalDashboardListResponse?.permissions ?? []), ...duplicatePermissions]}
        />
    );
  });

  // Dashboard List has two potential viewing states:
    // 1. Viewing all dashboards, Infegy dashboards are shown after user dashboards
    // 2. Viewing shared dashboards, Infegy dashboards are shown at the front of the list, followed by shared user dashboards
  // The logic for slicing the dashboards for pagination is different based on the state,
  // so we compute the paginated rows separately for each section and then combine them in the render.
  const pagedDashboardRows = useMemo(() => {
    if (!viewShared) { return dashboardRows }
    return dashboardRows.slice(sharedUserStartIndex, sharedUserEndIndex);
  }, [ viewShared, dashboardRows ]);

  const pagedGlobalDashboardOutputRows = useMemo(() => {
    // Shared view pins Infegy dashboards to the front of the combined experience.
    if (viewShared) {
      const sharedGlobalStartIndex = pageStartIndex;
      const sharedGlobalEndIndex = Math.min(pageEndIndex, globalDashboardOutputRows.length);
      if (sharedGlobalEndIndex <= sharedGlobalStartIndex) {
        return [];
      }
      return globalDashboardOutputRows.slice(sharedGlobalStartIndex, sharedGlobalEndIndex);
    }

    // Non-shared view appends Infegy dashboards after all user dashboards.
    const userDashboardTotal = dashboardListResponse?.pagination?.total ?? 0;
    const globalStartIndex = Math.max(pageStartIndex - userDashboardTotal, 0);
    const globalEndIndex = Math.max(Math.min(pageEndIndex - userDashboardTotal, globalDashboardOutputRows.length), 0);

    if (globalEndIndex <= globalStartIndex) {
      return [];
    }

    return globalDashboardOutputRows.slice(globalStartIndex, globalEndIndex);
  }, [ viewShared, globalDashboardOutputRows ]);

  // certain user types should not have certain dashboard types visible
  const showDashboardTypeOptions = !(user?.role === "quicksearch" || user?.role === "view-only");

  // Search isn't useful if the user only has a few dashboards
  const showSearchBox = (dashboardListResponse?.pagination?.total ?? 0) > 5;

  return (
    <Card style={{ display: 'flex', flexFlow: 'column', height: '100%' }}>
      { (showSearchBox || showDashboardTypeOptions) && (
      <CardHeader
        sx={{ py: 2, borderBottom: `1px solid ${theme.palette.divider}`, ".MuiCardHeader-action": { width: "100%" } }}
        action={
          <Stack
            direction="row"
            sx={{
              flexWrap: "wrap",
              gap: 1
            }}>
            {!isGettingStarted &&
              <TextField
                label={`${i18n.t('search')}...`}
                autoComplete="off"
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
                value={searchString || ""}
                onChange={((event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchString(event.target.value);
                  setCurrentPage(1);
              })}/>}
            <Select
              onChange={(event: SelectChangeEvent) => setDashboardType(event.target.value as DashboardType)}
              size="small"
              sx={{ mr: 1 }}
              value={dashboardType}
            >
              {dashboardTypeOptions.map((option) => {
                return <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              })}
            </Select>
            <FormControlLabel
              control={
                <AlignedSwitch
                  checked={viewShared}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setViewShared(event.target.checked);
                    setCurrentPage(1);
                  }}/>
              }
              label={i18n.t("resource-action.view-shared")}
            />
            {dashboardRows.length > 0 &&
              <Button
                variant="contained"
                size="small"
                endIcon={<Add />}
                color={canAddDashboards ? "primary" : "secondary"}
                onClick={() => {
                  if (canAddDashboards) {
                    setNewDashboardDialogIsOpen(true);
                  } else {
                    setShowInsufficientPermissionsDialog(true);
                  }
                }}
                sx={{ ml: "auto" }}
              >
                {i18n.t("add-new")}
              </Button>
            }
            <InsufficientPermissionsDialog
              open={showInsufficientPermissionsDialog}
              onClose={() => setShowInsufficientPermissionsDialog(false)}>
            </InsufficientPermissionsDialog>
          </Stack>
        }
      >
      </CardHeader>
      )}
      <CardContent sx={{pt: 1, pb: 2, px: 0, flex: 1, overflow: "auto", backgroundColor: theme.palette.background.default}}>
        {/* Loading */}
        {dashboardListIsLoading &&
          <Box sx={{ textAlign: "center", mt: 2, mb: 1 }}>
            <TextLoader />
          </Box>
        }

        {/* No Results */}
        {dashboardList.length === 0 && (searchString && searchString?.length > 0) &&
          <Box sx={{ textAlign: "center", mt: 2, mb: 1 }}>
            <Typography variant="h3">
              {i18n.t("widget-types.dashboard-list.no-search-results")}
            </Typography>
          </Box>
        }

        {/* Getting Started */}
        {isGettingStarted &&
          <Stack
            spacing={1}
            sx={{
              alignItems: "center",
              mb:3
            }}>
            <Typography variant="h3">
              {i18n.t("widget-types.dashboard-list.getting-started.title")}
            </Typography>
            <Typography variant="body1">
              {i18n.t("widget-types.dashboard-list.getting-started.info-no-dashboards")}
            </Typography>
            <Typography variant="body1">
              {i18n.t("widget-types.dashboard-list.getting-started.info-create-dashboard")}
            </Typography>
            <Button
                variant="contained"
                size="small"
                endIcon={<Add />}
                color={canAddDashboards ? "primary" : "secondary"}
                onClick={() => {
                  if (canAddDashboards) {
                    setNewDashboardDialogIsOpen(true);
                  } else {
                    setShowInsufficientPermissionsDialog(true);
                  }
                }}
              >
                {i18n.t("widget-types.dashboard-list.getting-started.create-dashboard")}
            </Button>
          </Stack>
        }

        {/* Dashboard Lists */}
        {(pagedDashboardRows.length > 0 || pagedGlobalDashboardOutputRows.length > 0) &&
          <Box>
            {(viewShared && pagedGlobalDashboardOutputRows.length > 0) &&
              <>
                <Typography variant="h4" sx={{ my: 1, ml: 2, color: theme.palette.text.secondary }} >
                  {i18n.t("infegy-provided-dashboards")}
                </Typography>
                <List sx={{ py: 0, overflowY: 'auto' }}>
                  {pagedGlobalDashboardOutputRows}
                </List>
              </>
            }

            {(viewShared && pagedDashboardRows.length > 0 && pagedGlobalDashboardOutputRows.length > 0) &&
              <Typography variant="h4" sx={{ my: 1, ml: 2, color: theme.palette.text.secondary }} >
                {i18n.t("all-accessible-dashboards")}
              </Typography>
            }

            <List sx={{ py: 0, height: '100%', overflowY: 'auto' }}>
              {pagedDashboardRows}
            </List>

            {(!viewShared && pagedGlobalDashboardOutputRows.length > 0) &&
              <>
                <Typography variant="h4" sx={{ my: 1, ml: 2, color: theme.palette.text.secondary }} >
                  {i18n.t("infegy-provided-dashboards")}
                </Typography>
                <List sx={{ py: 0, overflowY: 'auto' }}>
                  {pagedGlobalDashboardOutputRows}
                </List>
              </>
            }
          </Box>
        }

        {/* Pagination */}
        <CardActions sx={{ py: 0, justifyContent: 'center' }}>
          {totalPages > 1 &&
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event: React.ChangeEvent<unknown>, value: number) => { setCurrentPage(value) }}
              shape="rounded"
              sx={{display: 'flex', justifyContent: 'center', mt: 2}}
            />
          }
        </CardActions>
      </CardContent>
      {newDashboardDialogIsOpen &&
        <DashboardEditDialog
          setDialogIsOpen = {setNewDashboardDialogIsOpen}
          onClose = {fetchDashboards}
        />
      }
    </Card>
  );
}

export default withOptionsWidget(DashboardListContent, { hasSettingsDialog: false });